import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Ban, Eye, EyeOff, GripVertical, RotateCcw } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { usePlatform } from '@forq/platform';
import { applyLayoutOrder, emptyUiLayout, type UiLayout } from '@forq/platform-data';
import { useCustomize } from './CustomizeContext';
import { DEFAULT_TILE_COLOR, tileColorStyle } from './tileColor';

export interface CustomizableTile {
  /** Stabiler Schlüssel je Kachel -- bestimmt Reihenfolge/Sichtbarkeit; darf sich nie ändern. */
  readonly key: string;
  readonly node: ReactNode;
}

interface CustomizableMetricsProps {
  /** Stabiler Seiten-Schlüssel für die Persistenz (ui_layouts.page_key), z. B. "finance-cockpit". */
  readonly pageKey: string;
  /** Die Kacheln in ihrer Standard-Reihenfolge. */
  readonly tiles: readonly CustomizableTile[];
}

/** Eine sortierbare Kachel (dnd-kit): der Griff ist der Ziehpunkt, die Kachel rückt weich zur Seite. */
function SortableTile({ tile, hidden, color, onToggleHidden, onSetColor }: {
  readonly tile: CustomizableTile;
  readonly hidden: boolean;
  readonly color: string | null;
  readonly onToggleHidden: (key: string) => void;
  readonly onSetColor: (key: string, color: string | null) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tile.key });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 2 : undefined };
  return (
    <div ref={setNodeRef} style={style} className={`forq-metric-edit${isDragging ? ' is-dragging' : ''}${hidden ? ' is-hidden' : ''}`}>
      <div className="forq-metric-edit__bar">
        <span className="forq-metric-edit__grip" {...attributes} {...listeners} title="Ziehen zum Verschieben">
          <GripVertical size={13} />
        </span>
        <button type="button" className="forq-metric-edit__hide" onClick={() => onToggleHidden(tile.key)} title={hidden ? 'Wieder einblenden' : 'Ausblenden'}>
          {hidden ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
        <label className="forq-metric-edit__color" title="Kachelfarbe">
          <input type="color" value={color ?? DEFAULT_TILE_COLOR} onChange={(event) => onSetColor(tile.key, event.target.value)} />
        </label>
        {color ? (
          <button type="button" className="forq-metric-edit__color-clear" onClick={() => onSetColor(tile.key, null)} title="Farbe entfernen">
            <Ban size={13} />
          </button>
        ) : null}
      </div>
      {color ? <div className="forq-metric-color" style={tileColorStyle(color)}>{tile.node}</div> : tile.node}
    </div>
  );
}

/**
 * Kennzahlen-Reihe, die ein Admin im globalen Bearbeiten-Modus umsortieren und aus-/einblenden kann.
 * Umsortiert wird mit dnd-kit (weiches Live-Wegrücken beim Ziehen). Rendert bewusst NUR die Kacheln
 * (kein `forq-metric-row`-Rahmen) -- der Aufrufer setzt den Rahmen. Speichert je Mandant + Seite SOFORT
 * bei jeder Änderung (kein „Speichern"-Knopf, der globale Schalter beendet den Modus).
 */
export function CustomizableMetrics({ pageKey, tiles }: CustomizableMetricsProps) {
  const platform = usePlatform();
  const { tenantId, gateway } = useCustomize();
  const editing = platform.customizing && platform.permissions.has('admin.view');

  const [layout, setLayout] = useState<UiLayout>(emptyUiLayout);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    let active = true;
    gateway.loadLayout(tenantId, pageKey).then((loaded) => { if (active) setLayout(loaded); }).catch(() => {});
    return () => { active = false; };
  }, [gateway, tenantId, pageKey]);

  const ordered = useMemo(() => applyLayoutOrder(tiles, layout.order), [tiles, layout.order]);
  const hiddenSet = useMemo(() => new Set(layout.hidden), [layout.hidden]);

  // Optimistisch speichern: erst lokal setzen (sofortiges Feedback), dann persistieren; bei Fehler neu laden.
  const persist = useCallback((next: UiLayout) => {
    setLayout(next);
    setError(null);
    gateway.saveLayout(tenantId, pageKey, next)
      .then((saved) => setLayout(saved))
      .catch((saveError: unknown) => {
        setError(saveError instanceof Error ? saveError.message : 'Konnte nicht gespeichert werden.');
        gateway.loadLayout(tenantId, pageKey).then((loaded) => setLayout(loaded)).catch(() => {});
      });
  }, [gateway, tenantId, pageKey]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const keys = ordered.map((tile) => tile.key);
    const oldIndex = keys.indexOf(String(active.id));
    const newIndex = keys.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    persist({ ...layout, order: arrayMove(keys, oldIndex, newIndex) });
  }, [ordered, layout, persist]);

  const toggleHidden = useCallback((key: string) => {
    const next = new Set(hiddenSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    persist({ ...layout, hidden: [...next] });
  }, [hiddenSet, layout, persist]);

  const setColor = useCallback((key: string, color: string | null) => {
    const colors = { ...layout.colors };
    if (color) colors[key] = color; else delete colors[key];
    persist({ ...layout, colors });
  }, [layout, persist]);

  if (!editing) {
    return (
      <>
        {ordered.filter((tile) => !hiddenSet.has(tile.key)).map((tile) => {
          const color = layout.colors[tile.key];
          return color
            ? <div key={tile.key} className="forq-metric-color" style={tileColorStyle(color)}>{tile.node}</div>
            : <Fragment key={tile.key}>{tile.node}</Fragment>;
        })}
      </>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ordered.map((tile) => tile.key)} strategy={rectSortingStrategy}>
        {ordered.map((tile) => (
          <SortableTile key={tile.key} tile={tile} hidden={hiddenSet.has(tile.key)} color={layout.colors[tile.key] ?? null} onToggleHidden={toggleHidden} onSetColor={setColor} />
        ))}
      </SortableContext>
      <button type="button" className="forq-metric-reset" onClick={() => persist(emptyUiLayout)} title="Diese Reihe auf Standard zurücksetzen">
        <RotateCcw size={13} /> Standard
      </button>
      {error ? <div className="forq-metric-edit-error" role="alert">{error}</div> : null}
    </DndContext>
  );
}
